import { compact, includes, isEmpty } from 'lodash';

import { isAIDPWorkflowEnabled } from '@/modules/aiDP/aidp.utils';
import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { ArrayFieldWithID } from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useUpdateSubformArrayField';
import { getConfigsForAISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/matcherVariant.configs';
import { EntitySuggestionsContext } from '@/modules/aiSuggestions/context/EntitySuggestions.context';
import { EntityDetailsView } from '@/modules/entities/components/EntityDetails';
import {
  BusinessEntityDetailsTabs,
  NonTrustCharitableEntityDetailsTabs,
  PersonalAccountDetailsTabs,
  TrustDetailsTabs,
} from '@/modules/entities/details/entityDetails.types';
import {
  ENTITY_SECTION_TO_SUBFORM_MAP,
  ENTITY_SUBFORM_TO_SECTION_MAP,
  NO_ENTITY_SECTIONS,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.constants';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntityRichSection,
  EditEntitySection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  INSURANCE_POLICY_ENTITY_TYPES,
  NON_TRUST_CHARITABLE_ENTITY_TYPES,
  PERSONAL_FAMILY_ACCOUNT_TYPES,
} from '@/modules/entities/entities.constants';
import {
  EntitySubformNamespaceType,
  SUBFORM_TYPE_COPY_MAP,
} from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import {
  makeUpdateEntityInput,
  MakeUpdateEntityInputParams,
} from '@/modules/entities/EntitySubforms/updateUtils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind, AugmentedUpdateEntityInput } from '@/types/schema';
import { UnreachableError, ValidationError } from '@/utils/errors';

import { isCurrentBeneficiaryEntityKind } from '../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { getEntityKindFromEntityType } from '../utils/getEntityKindFromEntityType';

/**
 * Query param naming which entity-details tab ({@link EntityDetailsView}) to
 * open after a successful save in the edit-entity trowser. Openers that want a
 * post-save jump set it (only the insurance-policies valuation action today);
 * `EditEntitySplitScreenForm` reads it on close and navigates there.
 */
export const NAVIGATE_TO_ENTITY_TAB_AFTER_SAVE_PARAM =
  'navigateToEntityTabAfterSave';

export function getSectionsForEntityType(
  entityType: EntityType
): EditEntitySection[] {
  switch (entityType) {
    // TRUSTS
    // Note: DISPOSITIVE_PROVISIONS_GRANTOR_2 is intentionally omitted - death order is selected within the form
    case 'revocable-trust':
    case 'irrevocable-trust':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'slat':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.LIFETIME_BENEFICIARIES,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'ilit':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'qprt':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.INITIAL_FUNDING_ASSETS,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'grat':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.TRUSTEES,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'clt':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.LEAD_BENEFICIARIES,
        EditEntitySection.REMAINDER_BENEFICIARIES,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'crt':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TRUSTEES,
        EditEntitySection.INCOME_BENEFICIARIES,
        EditEntitySection.REMAINDER_BENEFICIARIES,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    // BUSINESS ENTITIES
    case 'sole-proprietorship':
    case 'c-corp':
    case 's-corp':
    case 'llc':
    case 'lp':
    case 'gp': {
      // not all business entities have insurance policies
      const shouldShowInsurancePolicies = includes(
        INSURANCE_POLICY_ENTITY_TYPES,
        entityType
      );

      return compact([
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.BUSINESS_ENTITY_TAX_STATUS,
        EditEntitySection.BUSINESS_ENTITY_DETAILS,
        shouldShowInsurancePolicies && EditEntitySection.INSURANCE_POLICIES,
      ]);
    }
    // NON-TRUST CHARITABLE ENTITIES
    case 'private-foundation':
    case 'daf':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    // PERSONAL ACCOUNTS
    case 'individual-account':
    case 'joint-account':
    case 'custodial-account':
    case 'qualified-tuition-account':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.TAX_STATUS,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
      ];
    case 'retirement-account':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.BENEFICIARIES,
        EditEntitySection.BENEFICIARY_DESIGNATIONS,
      ];
    case 'insurance-account':
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.INSURANCE_POLICIES,
        EditEntitySection.BENEFICIARY_DESIGNATIONS,
      ];
    default:
      throw new UnreachableError({
        case: entityType,
        message: `Unreachable entityType: ${entityType} in getSectionsForEntityType`,
      });
  }
}

export function getSectionForEntitySubformType(
  subformType: EntitySubformNamespaceType
): EditEntitySection {
  if (!ENTITY_SUBFORM_TO_SECTION_MAP[subformType]) {
    // We'll add more cases here as we implement support for more entity types
    // that have additional sections.
    throw new Error(`Unsupported subform type: ${subformType}`);
  }

  return ENTITY_SUBFORM_TO_SECTION_MAP[subformType];
}

export function getEntitySubformTypeForSection(
  section: EditEntitySection
): EntitySubformNamespaceType {
  const subformType = ENTITY_SECTION_TO_SUBFORM_MAP[section];
  if (!subformType) {
    throw new Error(`Unsupported entity section: ${section}`);
  }

  if (subformType === NO_ENTITY_SECTIONS) {
    // This is type protection to make sure we don't call this in a dispositive provisions scenario.
    throw new Error(`${section} does not have a corresponding entity subform`);
  }

  return subformType;
}

export function getLabelForSection(
  section: EditEntitySection,
  entityType: EntityType
): string | null {
  // BDG section — always labeled "Beneficiary designations"
  if (section === EditEntitySection.BENEFICIARY_DESIGNATIONS) {
    return 'Beneficiary designations';
  }

  // Consolidated dispositions menu item - death order is selected within the form
  if (section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1) {
    if (
      entityType === 'retirement-account' ||
      entityType === 'insurance-account'
    ) {
      return 'Beneficiary designations';
    }
    return 'Dispositions';
  }

  // GRANTOR_2 is no longer shown in the menu - death order is selected within the form
  if (section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2) {
    return null;
  }

  const subformType = getEntitySubformTypeForSection(section);

  if (
    subformType === 'beneficiariesSubform' &&
    (entityType === 'grat' || entityType === 'qprt')
  ) {
    // Special-case handling for GRAT and QPRT Beneficiaries section.
    return 'Remainder beneficiaries';
  }

  if (subformType === 'businessEntityDetailsSubform') {
    // Special-case handling for business entities "details" subform.
    // Which are the ownership and key people fields.
    // Because SUBFORM_TYPE_COPY_MAP contains the label for the old long-form trowser.
    return 'Ownership & key people';
  }

  if (subformType === 'trustDetailsSubform') {
    // this is necessary while we're still in the process of migrating to the new
    // form interface; in the trust details subform should still be called "Trust details"
    // in the context where the new form interface is not yet implemented
    return 'Trustees';
  }

  if (subformType === 'beneficiariesSubform') {
    const entityKind = getEntityKindFromEntityType(entityType);
    if (isCurrentBeneficiaryEntityKind(entityKind)) {
      return 'Current beneficiaries';
    }
  }

  return SUBFORM_TYPE_COPY_MAP[subformType];
}

export function getSectionOptions(
  entityType: EntityType | null,
  primaryClients: ContextPrimaryClient[] | null,
  shownSectionOptions?: EditEntitySection[] | null
): EditEntityRichSection[] {
  // For typesafety and on initial render. We should always have an entityType and primaryClients.
  if (isEmpty(primaryClients) || !primaryClients || !entityType) return [];

  const validEntitySections = getSectionsForEntityType(entityType);
  const sectionsToRender = shownSectionOptions
    ? shownSectionOptions.filter((section) =>
        validEntitySections.includes(section)
      )
    : validEntitySections;

  return sectionsToRender.flatMap((section) => {
    const label = getLabelForSection(section, entityType);
    if (!label) return [];

    // The consolidated "Dispositions" option also represents the second-death
    // DP section, which has no menu entry of its own — the death order is
    // selected within the DP form.
    const representedSections =
      section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
        ? [section, EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2]
        : [section];

    return {
      label,
      section,
      representedSections,
    };
  });
}

/**
 * Returns the next/previous section relative to the current section within
 * the given section options, wrapping around at either end. Falls back to the
 * current section when it can't be resolved against the options.
 *
 * @param direction
 * @param currentSection
 * @param sectionOptions
 */
export function getAdjacentSectionOption(
  direction: 'next' | 'prev',
  currentSection: EditEntitySection,
  sectionOptions: EditEntityRichSection[]
): EditEntitySection {
  if (isEmpty(sectionOptions)) return currentSection;

  const currentIndex = sectionOptions.findIndex((option) =>
    option.representedSections.includes(currentSection)
  );
  if (currentIndex === -1) return currentSection;

  const offset = direction === 'next' ? 1 : -1;
  const targetIndex =
    (currentIndex + offset + sectionOptions.length) % sectionOptions.length;
  return sectionOptions[targetIndex]?.section ?? currentSection;
}

/**
 * This function returns corresponding edit entity sections for the active tab
 * of the entity details page. This is called from getInitialSectionOptions()
 * to know the section(s) to show when the user clicks the "edit" button to
 * open the edit entity split screen modal. If there's multiple corresponding
 * sections, the "edit" button will be a dropdown button. If there's only 1
 * section, we show a full-width button.
 *
 * @param activeTab
 * @param entityType
 */
export function getSectionForEntityDetailsActiveTab(
  activeTab: EntityDetailsView,
  entityType: EntityType
): EditEntitySection[] {
  switch (activeTab) {
    // TRUSTS TABS
    case TrustDetailsTabs.SUMMARY:
      switch (entityType) {
        case 'qprt':
          return [
            EditEntitySection.BASIC_INFORMATION,
            EditEntitySection.TAX_STATUS,
            EditEntitySection.INITIAL_FUNDING_ASSETS,
          ];
        case 'crt':
          return [EditEntitySection.BASIC_INFORMATION];
        default:
          return [
            EditEntitySection.BASIC_INFORMATION,
            EditEntitySection.TAX_STATUS,
          ];
      }
    case TrustDetailsTabs.TRUSTEES:
      return [EditEntitySection.TRUSTEES];
    case TrustDetailsTabs.BENEFICIARIES:
      switch (entityType) {
        case 'slat':
          return [EditEntitySection.LIFETIME_BENEFICIARIES];
        case 'clt':
          return [
            EditEntitySection.LEAD_BENEFICIARIES,
            EditEntitySection.REMAINDER_BENEFICIARIES,
          ];
        case 'crt':
          return [
            EditEntitySection.INCOME_BENEFICIARIES,
            EditEntitySection.REMAINDER_BENEFICIARIES,
          ];
        default:
          return [EditEntitySection.BENEFICIARIES];
      }
    case TrustDetailsTabs.DISPOSITIONS:
      // Single dispositions entry - death order is selected within the form
      return [EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1];
    case TrustDetailsTabs.POLICIES:
    case BusinessEntityDetailsTabs.POLICIES:
    case NonTrustCharitableEntityDetailsTabs.POLICIES:
    case PersonalAccountDetailsTabs.POLICIES:
      return [EditEntitySection.INSURANCE_POLICIES];
    // BUSINESS ENTITIES TABS
    case BusinessEntityDetailsTabs.SUMMARY:
      return [
        EditEntitySection.BASIC_INFORMATION,
        EditEntitySection.BUSINESS_ENTITY_TAX_STATUS,
      ];
    case BusinessEntityDetailsTabs.KEY_PEOPLE:
    case BusinessEntityDetailsTabs.OWNERSHIP:
      return [EditEntitySection.BUSINESS_ENTITY_DETAILS];
    // NON-TRUST CHARITABLE ENTITY TABS
    case NonTrustCharitableEntityDetailsTabs.SUMMARY:
      return [EditEntitySection.BASIC_INFORMATION];
    case NonTrustCharitableEntityDetailsTabs.BENEFICIARIES:
      return [EditEntitySection.BENEFICIARIES];
    case NonTrustCharitableEntityDetailsTabs.DISPOSITIONS:
      return [EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1];
    // PERSONAL ACCOUNTS TABS
    case PersonalAccountDetailsTabs.SUMMARY:
      switch (entityType) {
        case 'retirement-account':
        case 'insurance-account':
          return [EditEntitySection.BASIC_INFORMATION];
        default:
          return [
            EditEntitySection.BASIC_INFORMATION,
            EditEntitySection.TAX_STATUS,
          ];
      }
    case PersonalAccountDetailsTabs.BENEFICIARIES:
      return [EditEntitySection.BENEFICIARIES];
    case PersonalAccountDetailsTabs.DISPOSITIONS:
      if (
        entityType === 'retirement-account' ||
        entityType === 'insurance-account'
      ) {
        return [EditEntitySection.BENEFICIARY_DESIGNATIONS];
      }
      // Single dispositions entry - death order is selected within the form
      return [EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1];
    default:
      throw new UnreachableError({
        case: activeTab,
        message: `Unreachable activeTab: ${activeTab} in getSectionForEntityDetailsActiveTab`,
      });
  }
}

/**
 * This wraps the call to getSectionForEntityDetailsActiveTab() to ensure that
 * we don't show the "edit" button on the Dispositions tab, by returning undefined.
 *
 * @param activeTab
 * @param entityType
 */
export function getInitialSectionOptions(
  activeTab: EntityDetailsView | '',
  entityType: EntityType | null
): EditEntitySection[] | undefined {
  if (
    !entityType ||
    !activeTab ||
    activeTab === NonTrustCharitableEntityDetailsTabs.DISPOSITIONS
  ) {
    return undefined;
  }

  const sections = getSectionForEntityDetailsActiveTab(activeTab, entityType);

  return isEmpty(sections) ? undefined : sections;
}

/**
 * This is the tab of the entity details page to navigate to after saving and closing the modal.
 * @param entityType
 * @param section the section that was last saved and closed on
 */
export function getActiveTabForSection(
  entityType: EntityType,
  section: EditEntitySection
): EntityDetailsView {
  switch (section) {
    case EditEntitySection.BASIC_INFORMATION:
      if (NON_TRUST_CHARITABLE_ENTITY_TYPES.includes(entityType)) {
        return NonTrustCharitableEntityDetailsTabs.SUMMARY;
      }
      if (PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType)) {
        return PersonalAccountDetailsTabs.SUMMARY;
      }
      return TrustDetailsTabs.SUMMARY;
    case EditEntitySection.BENEFICIARIES:
      if (NON_TRUST_CHARITABLE_ENTITY_TYPES.includes(entityType)) {
        return NonTrustCharitableEntityDetailsTabs.BENEFICIARIES;
      }
      if (PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType)) {
        return PersonalAccountDetailsTabs.BENEFICIARIES;
      }
      return TrustDetailsTabs.BENEFICIARIES;
    case EditEntitySection.TAX_STATUS:
      if (NON_TRUST_CHARITABLE_ENTITY_TYPES.includes(entityType)) {
        return NonTrustCharitableEntityDetailsTabs.SUMMARY;
      }
      if (PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType)) {
        return PersonalAccountDetailsTabs.SUMMARY;
      }
      return TrustDetailsTabs.SUMMARY;
    case EditEntitySection.TRUSTEES:
      return TrustDetailsTabs.TRUSTEES;
    case EditEntitySection.INSURANCE_POLICIES:
      return TrustDetailsTabs.POLICIES;
    case EditEntitySection.INITIAL_FUNDING_ASSETS:
    case EditEntitySection.ANNUITY_PAYMENTS:
      return TrustDetailsTabs.SUMMARY;
    case EditEntitySection.LIFETIME_BENEFICIARIES:
    case EditEntitySection.REMAINDER_BENEFICIARIES:
    case EditEntitySection.INCOME_BENEFICIARIES:
    case EditEntitySection.LEAD_BENEFICIARIES:
      return TrustDetailsTabs.BENEFICIARIES;
    case EditEntitySection.BUSINESS_ENTITY_TAX_STATUS:
      return BusinessEntityDetailsTabs.SUMMARY;
    case EditEntitySection.BUSINESS_ENTITY_DETAILS:
      return BusinessEntityDetailsTabs.KEY_PEOPLE;
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1:
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2:
      if (PERSONAL_FAMILY_ACCOUNT_TYPES.includes(entityType)) {
        return PersonalAccountDetailsTabs.DISPOSITIONS;
      }
      return TrustDetailsTabs.DISPOSITIONS;
    case EditEntitySection.BENEFICIARY_DESIGNATIONS:
      return PersonalAccountDetailsTabs.DISPOSITIONS;
  }
}

/**
 * Returns the grantor ID for the given section.
 *
 * @param section
 * @param primaryClients
 */
export function getGrantorIdForSection(
  section: EditEntitySection,
  primaryClients: ContextPrimaryClient[] | null
): string {
  // this should never happen in a real scenario, doing it for typesafety
  if (!primaryClients) return '';
  switch (section) {
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1:
      return primaryClients[0]?.id ?? '';
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2:
      return primaryClients[1]?.id ?? '';
    default:
      throw new Error(
        `getGrantorIdForSection called with invalid section: ${section}. only DP sections are supported`
      );
  }
}

export function getUpdateInputFromFormValuesOrError(
  params: MakeUpdateEntityInputParams,
  values: CombinedSupportedSubformFieldTypes
): {
  input?: AugmentedUpdateEntityInput;
  validationError?: ValidationError;
  error?: Error;
} {
  try {
    const updateEntityInput = makeUpdateEntityInput(values, params);
    return { input: updateEntityInput };
  } catch (err) {
    if (err instanceof ValidationError) {
      // Validation error to let the user know what to fix in the form.
      return { validationError: err };
    }

    // Some unexpected error (for devs).
    return { error: err as Error };
  }
}

export function getMatcherVariantForEditEntitySection(
  entityType: EntityType | null | undefined,
  editEntitySection: EditEntitySection
): AISuggestionsMatcherVariant | undefined {
  switch (editEntitySection) {
    case EditEntitySection.BASIC_INFORMATION:
      if (entityType === 'slat') {
        return AISuggestionsMatcherVariant.SINGLE_GRANTOR;
      }
      return AISuggestionsMatcherVariant.GRANTORS;
    case EditEntitySection.TRUSTEES:
      return AISuggestionsMatcherVariant.TRUSTEES;
    case EditEntitySection.BENEFICIARIES:
      return AISuggestionsMatcherVariant.BENEFICIARIES_V2;
    case EditEntitySection.LIFETIME_BENEFICIARIES:
      return AISuggestionsMatcherVariant.BENEFICIARIES_V2;
    case EditEntitySection.BUSINESS_ENTITY_DETAILS:
      return AISuggestionsMatcherVariant.BUSINESS_PEOPLE;
    default:
      // Other sections don't require matching AI suggestions with existing records.
      return undefined;
  }
}

/**
 * Returns true if there are any unacknowledged AI suggestions for the given
 * entity type and section.
 *
 * @param entityType
 * @param section
 * @param suggestionsByKind
 */
export function getSectionHasUnacknowledgedSuggestions(
  entityType: EntityType | null | undefined,
  section: EditEntitySection,
  suggestionsByKind: Partial<EntitySuggestionsContext['suggestionsByKind']>
): boolean {
  return getAISuggestionKindsForEntitySection(entityType, section).some(
    (kind) => (suggestionsByKind[kind] ?? []).some((s) => !s.acceptanceStatus)
  );
}

/**
 * Returns the AI suggestion kinds that are on the given EditEntitySection.
 *
 * @param entityType
 * @param section
 */
export function getAISuggestionKindsForEntitySection(
  entityType: EntityType | null | undefined,
  section: EditEntitySection
): AiSuggestionKind[] {
  switch (section) {
    case EditEntitySection.BASIC_INFORMATION:
      return [
        AiSuggestionKind.EffectiveDate,
        AiSuggestionKind.JurisdictionStateCode,
        AiSuggestionKind.LegalName,
        AiSuggestionKind.Principal,
        AiSuggestionKind.DoingBusinessAsName,
        AiSuggestionKind.FincenId,
      ];
    case EditEntitySection.TRUSTEES:
      return [
        AiSuggestionKind.Trustee,
        AiSuggestionKind.SuccessorTrustee,
        AiSuggestionKind.TrustAdvisor,
      ];
    case EditEntitySection.TAX_STATUS:
      return [AiSuggestionKind.GstStatus];
    case EditEntitySection.BENEFICIARIES:
    case EditEntitySection.LIFETIME_BENEFICIARIES:
    case EditEntitySection.REMAINDER_BENEFICIARIES:
      return [AiSuggestionKind.BeneficiaryV2];
    case EditEntitySection.INSURANCE_POLICIES:
      return [AiSuggestionKind.CrummeyWithdrawalPeriodDays];
    case EditEntitySection.BUSINESS_ENTITY_DETAILS:
      return [
        AiSuggestionKind.BeneficialOwner,
        AiSuggestionKind.BeneficialOwnersAsOfDate,
        AiSuggestionKind.KeyPerson,
      ];
    case EditEntitySection.BUSINESS_ENTITY_TAX_STATUS:
      return [AiSuggestionKind.TaxId, AiSuggestionKind.BusinessEntityTaxStatus];
    case EditEntitySection.INCOME_BENEFICIARIES:
    case EditEntitySection.LEAD_BENEFICIARIES:
    case EditEntitySection.INITIAL_FUNDING_ASSETS:
    case EditEntitySection.ANNUITY_PAYMENTS:
      return [];
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1:
      // Include both death order suggestions since we consolidated to a single "Dispositions" menu item
      // The death order is now selected within the form rather than via separate menu items
      if (!isAIDPWorkflowEnabled(entityType)) {
        return [];
      }
      return [AiSuggestionKind.DpRecipientV2];
    case EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2:
      // GRANTOR_2 is no longer shown in the menu - death order is selected within the form
      // Return empty array since suggestions are now handled by GRANTOR_1 section
      return [];
    case EditEntitySection.BENEFICIARY_DESIGNATIONS:
      return [];
    default:
      return [];
  }
}

/**
 * Returns all suggestions for the kinds shown in an entity section.
 */
export function getAISuggestionsForEntitySection<T>(
  entityType: EntityType | null | undefined,
  currentSection: EditEntitySection,
  suggestionsByKind: Record<string, T[]>
): T[] {
  const suggestionKindsForCurrentSection = getAISuggestionKindsForEntitySection(
    entityType,
    currentSection
  );

  return suggestionKindsForCurrentSection.flatMap(
    (kind) => suggestionsByKind[kind] ?? []
  );
}

/**
 * Returns the array fields that we need to check for duplicates, before we can
 * submit the given section form.
 *
 * @param entityType
 * @param section
 */
export function getArrayFieldsWithIDForSection(
  entityType: EntityType | null | undefined,
  section: EditEntitySection
): ArrayFieldWithID[] {
  const suggestionsMatcherVariant = getMatcherVariantForEditEntitySection(
    entityType,
    section
  );

  if (!suggestionsMatcherVariant) {
    return [];
  }

  const configs = getConfigsForAISuggestionsMatcherVariant(
    entityType,
    suggestionsMatcherVariant
  );
  if (!configs) {
    return [];
  }

  return configs.subformArrayFieldUpdateProps.map((config) => ({
    arrayFieldFullPath: config.arrayFieldFullPath,
    idField: config.idField,
  }));
}
